/* eslint-disable react-hooks/exhaustive-deps */
import { useFormikContext } from "formik";
import { Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DateInput from "../../../components/form/DateInput";
import ImageInput from "../../../components/form/ImageInput/ImageInput";
import SelectInput from "../../../components/form/SelectInput";
import TextInput from "../../../components/form/TextInput";
import VStack from "../../../components/layout/VStack/VStack";
import Text from "../../../components/typography/Text/Text";
import { useFileUpload } from "../../../hooks/useFileUpload";
import provinceJson from "../../Json/allprovince.json";

export const EditProfileForm = ({
  index,
  userData,
  dataType,
  touched,
  errors,
  remove,
  canRemove,
  districtOptions,
  subDistrictOptions,
  setDistrictOptions,
  setSubDistrictOptions,
}) => {
  const { t, i18n } = useTranslation("requirement");
  const lang = i18n.language.startsWith("en") ? "en" : "th";
  const { setFieldValue, values } = useFormikContext();
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const isForeigner = userData.is_foreigner;

  const provinceData = provinceJson;

  const {
    fileInfo: prefixFile,
    propagateFileChange: prefixPropagateFileChange,
    onSetFileInfo: prefixOnSetFileInfo,
  } = useFileUpload("ใบรับรองสถานะภาพสมรส", null);

  useEffect(() => {
    if (prefixFile) {
      setFieldValue(`details.${index}.file`, prefixFile);
    }
  }, [index, prefixFile]);

  const {
    fileInfo: nameFile,
    propagateFileChange: namePropagateFileChange,
    onSetFileInfo: nameOnSetFileInfo,
  } = useFileUpload("หนังสือสำคัญการเปลี่ยนชื่อ", null);

  useEffect(() => {
    if (nameFile) {
      setFieldValue(`details.${index}.file`, nameFile);
    }
  }, [index, nameFile]);

  const {
    fileInfo: lastNameFile,
    propagateFileChange: lastNamePropagateFileChange,
    onSetFileInfo: lastNameOnSetFileInfo,
  } = useFileUpload("หนังสือสำคัญการเปลี่ยนนามสกุล", null);

  useEffect(() => {
    if (lastNameFile) {
      setFieldValue(`details.${index}.file`, lastNameFile);
    }
  }, [index, lastNameFile]);

  useEffect(() => {
    if (userData && !selectedProvince) {
      const provinceId = userData.province_id?.toString();
      const districtId = userData.district_id?.toString();
      if (provinceId) {
        setSelectedProvince(provinceId);
        const province = provinceData.find(
          (p) => p.id === parseInt(provinceId)
        );
        if (province) {
          setDistrictOptions(province.amphure || []);
          if (districtId) {
            setSelectedDistrict(districtId);
            const district = province.amphure?.find(
              (d) => d.id === parseInt(districtId)
            );
            if (district) {
              setSubDistrictOptions(district.tambon || []);
            }
          }
        }
      }
    }
  }, [provinceData, selectedProvince, userData]);

  // Auto-detect districts when province changes
  useEffect(() => {
    if (selectedProvince) {
      const province = provinceData.find(
        (p) => p.id === parseInt(selectedProvince)
      );
      if (province) {
        setDistrictOptions(province.amphure || []);
      }
    }
  }, [provinceData, selectedProvince]);

  // Auto-detect sub-districts when district changes
  useEffect(() => {
    if (selectedProvince && selectedDistrict) {
      const province = provinceData.find(
        (p) => p.id === parseInt(selectedProvince)
      );
      const district = province?.amphure?.find(
        (d) => d.id === parseInt(selectedDistrict)
      );
      if (district) {
        setSubDistrictOptions(district.tambon || []);
      }
    }
  }, [selectedProvince, selectedDistrict, provinceData]);

  const handleProvinceChange = (event) => {
    const selectedProvinceId = event.target.value;
    setSelectedProvince(selectedProvinceId);
    setSelectedDistrict(null);

    const province = provinceData.find(
      (p) => p.id === parseInt(selectedProvinceId)
    );
    if (province) {
      setFieldValue(`details.${index}.province`, selectedProvinceId);
      setDistrictOptions(province.amphure || []);
      setSubDistrictOptions([]);
      setFieldValue(`details.${index}.district`, "");
      setFieldValue(`details.${index}.subDistrict`, "");
      setFieldValue(`details.${index}.zipCode`, "");

      if (province.amphure && province.amphure.length > 0) {
        const firstDistrict = province.amphure[0];
        setSelectedDistrict(firstDistrict.id.toString());
        setFieldValue(`details.${index}.district`, firstDistrict.id.toString());

        if (firstDistrict.tambon && firstDistrict.tambon.length > 0) {
          const firstSubDistrict = firstDistrict.tambon[0];
          setFieldValue(
            `details.${index}.subDistrict`,
            firstSubDistrict.id.toString()
          );
          setFieldValue(`details.${index}.zipCode`, firstSubDistrict.zip_code);
        }
      }
    }
  };

  const handleDistrictChange = (event) => {
    const selectedDistrictId = event.target.value;
    setSelectedDistrict(selectedDistrictId);

    const selectedDistrict = selectedProvince
      ? provinceData
          .find((p) => p.id === parseInt(selectedProvince))
          ?.amphure.find((d) => d.id === parseInt(selectedDistrictId))
      : null;

    if (selectedDistrict) {
      setSubDistrictOptions(selectedDistrict.tambon || []);
      setFieldValue(`details.${index}.district`, selectedDistrictId);
      setFieldValue(`details.${index}.subDistrict`, "");
      setFieldValue(`details.${index}.zipCode`, "");

      if (selectedDistrict.tambon && selectedDistrict.tambon.length > 0) {
        const firstSubDistrict = selectedDistrict.tambon[0];
        setFieldValue(
          `details.${index}.subDistrict`,
          firstSubDistrict.id.toString()
        );
        setFieldValue(`details.${index}.zipCode`, firstSubDistrict.zip_code);
      }
    }
  };

  const handleSubDistrictChange = (event) => {
    const selectedSubDistrictId = event.target.value;
    const selectedSubDistrict = subDistrictOptions.find(
      (subdistrict) => subdistrict.id === parseInt(selectedSubDistrictId)
    );

    if (selectedSubDistrict) {
      setFieldValue(`details.${index}.subDistrict`, selectedSubDistrictId);
      setFieldValue(`details.${index}.zipCode`, selectedSubDistrict.zip_code);
    }
  };

  const dataTypeOptions = useMemo(() => {
    const dataTypes = values.details.map((detail) => detail.dataType);
    return [
      {
        value: "prefix",
        label: t("editProfile.prefix"),
        disabled: dataTypes.includes("prefix") && dataType !== "prefix",
      },
      {
        value: "contact",
        label: t("editProfile.contact"),
        disabled: dataTypes.includes("contact") && dataType !== "contact",
      },
      {
        value: "name",
        label: t("editProfile.name"),
        disabled: dataTypes.includes("name") && dataType !== "name",
      },
      {
        value: "lastName",
        label: t("editProfile.lastName"),
        disabled: dataTypes.includes("lastName") && dataType !== "lastName",
      },
    ];
  }, [dataType, t, values.details]);

  return (
    <VStack
      spacing={16}
      style={{ padding: 20, backgroundColor: "#F2F4F7", position: "relative" }}
    >
      {canRemove && (
        <Trash2
          onClick={remove}
          style={{
            position: "absolute",
            width: 20,
            height: 20,
            top: 20,
            right: 20,
            cursor: "pointer",
            color: "red",
          }}
        />
      )}
      <Text weight={600}>
        {t("editProfile.item")} {index + 1}
      </Text>
      <SelectInput
        label={t("editProfile.selectDataType")}
        name={`details.${index}.dataType`}
        required
        onChange={(e) => {
          setFieldValue(`details.${index}.file`, null);
          prefixOnSetFileInfo(null);
          nameOnSetFileInfo(null);
          lastNameOnSetFileInfo(null);
        }}
        options={dataTypeOptions}
        placeholder={t("editProfile.dataTypePlaceholder")}
        errorMessage={touched?.dataType && errors?.dataType}
      />

      {dataType === "prefix" && (
        <>
          <SelectInput
            label={t("editProfile.prefix")}
            name={`details.${index}.prefix`}
            required
            options={[
              { value: "นาย", label: t("editProfile.mr") },
              { value: "นาง", label: t("editProfile.mrs") },
              { value: "นางสาว", label: t("editProfile.miss") },
            ]}
            placeholder={t("editProfile.prefixPlaceholder")}
            errorMessage={errors?.prefix}
          />
          <SelectInput
            label={t("editProfile.status")}
            name={`details.${index}.status`}
            required
            options={[
              { value: "single", label: t("editProfile.single") },
              { value: "married", label: t("editProfile.married") },
              { value: "divorced", label: t("editProfile.divorced") },
              { value: "widowed", label: t("editProfile.widowed") },
            ]}
            placeholder={t("editProfile.statusPlaceholder")}
            errorMessage={errors?.status}
          />
          <ImageInput
            label={t("editProfile.prefixUploadLabel")}
            description={t("editProfile.prefixUploadDesc")}
            {...prefixPropagateFileChange()}
            fileInfo={prefixFile}
            placeholder={t("editProfile.prefixUploadPlaceholder")}
            errorMessage={
              !prefixFile?.file && t("editProfile.prefixUploadLabel")
            }
          />
        </>
      )}

      {dataType === "name" && (
        <>
          <TextInput
            label={t("editProfile.firstNameTh")}
            name={`details.${index}.firstNameTh`}
            required={!isForeigner}
            placeholder={t("editProfile.firstNameThPlaceholder")}
            errorMessage={errors?.firstNameTh}
          />

          <TextInput
            label={t("editProfile.firstNameEn")}
            name={`details.${index}.firstNameEn`}
            required={isForeigner}
            placeholder={t("editProfile.firstNameEnPlaceholder")}
            errorMessage={errors?.firstNameEn}
          />
          <TextInput
            label={t("editProfile.middleNameTh")}
            name={`details.${index}.middleNameTh`}
            placeholder={t("editProfile.middleNameThPlaceholder")}
            errorMessage={errors?.middleNameTh}
          />
          <TextInput
            label={t("editProfile.middleNameEn")}
            name={`details.${index}.middleNameEn`}
            placeholder={t("editProfile.middleNameEnPlaceholder")}
            errorMessage={errors?.middleNameEn}
          />
          <ImageInput
            label={t("editProfile.nameUploadLabel")}
            description={t("editProfile.nameUploadDesc")}
            {...namePropagateFileChange()}
            fileInfo={nameFile}
            placeholder={t("editProfile.nameUploadPlaceholder")}
            errorMessage={!nameFile?.file && t("editProfile.nameUploadLabel")}
          />
        </>
      )}

      {dataType === "lastName" && (
        <>
          <TextInput
            label={t("editProfile.lastNameTh")}
            name={`details.${index}.lastNameTh`}
            required={!isForeigner}
            placeholder={t("editProfile.lastNameThPlaceholder")}
            errorMessage={errors?.lastNameTh}
          />
          <TextInput
            label={t("editProfile.lastNameEn")}
            name={`details.${index}.lastNameEn`}
            required={isForeigner}
            placeholder={t("editProfile.lastNameEnPlaceholder")}
            errorMessage={errors?.lastNameEn}
          />
          <ImageInput
            label={t("editProfile.lastNameUploadLabel")}
            description={t("editProfile.lastNameUploadDesc")}
            {...lastNamePropagateFileChange()}
            fileInfo={lastNameFile}
            placeholder={t("editProfile.lastNameUploadPlaceholder")}
            errorMessage={
              !lastNameFile?.file && t("editProfile.lastNameUploadLabel")
            }
          />
        </>
      )}

      {dataType === "contact" && (
        <>
          <DateInput
            label={t("editProfile.birthDate")}
            name={`details.${index}.birthDate`}
            required
            errorMessage={errors?.birthDate}
          />

          <TextInput
            label={t("editProfile.phone")}
            name={`details.${index}.phone`}
            type="tel"
            required
            placeholder={t("editProfile.phonePlaceholder")}
            maxLength={10}
            errorMessage={errors?.phone}
          />

          <TextInput
            label={t("editProfile.email")}
            name={`details.${index}.email`}
            type="email"
            required
            placeholder={t("editProfile.emailPlaceholder")}
            errorMessage={errors?.email}
          />

          <Text weight={600}>{t("editProfile.addressTitle")}</Text>

          <TextInput
            label={t("editProfile.currentAddress")}
            name={`details.${index}.currentAddress`}
            required
            placeholder={t("editProfile.currentAddressPlaceholder")}
            errorMessage={errors?.currentAddress}
          />

          <SelectInput
            label={t("editProfile.province")}
            name={`details.${index}.province`}
            required
            options={provinceData.map((province) => ({
              value: province.id.toString(),
              label: lang === "en" ? province.name_en : province.name_th,
            }))}
            placeholder={t("editProfile.provincePlaceholder")}
            errorMessage={errors?.province}
            onChange={(e) => handleProvinceChange(e, setFieldValue)}
          />

          <SelectInput
            label={t("editProfile.district")}
            name={`details.${index}.district`}
            required
            options={districtOptions.map((district) => ({
              value: district.id.toString(),
              label: lang === "en" ? district.name_en : district.name_th,
            }))}
            placeholder={t("editProfile.districtPlaceholder")}
            errorMessage={errors?.district}
            onChange={(e) => {
              setFieldValue("district", e.target.value);
              handleDistrictChange(e, setFieldValue);
            }}
          />

          <SelectInput
            label={t("editProfile.subDistrict")}
            name={`details.${index}.subDistrict`}
            required
            options={subDistrictOptions.map((subDistrict) => ({
              value: subDistrict.id.toString(),
              label: lang === "en" ? subDistrict.name_en : subDistrict.name_th,
            }))}
            placeholder={t("editProfile.subDistrictPlaceholder")}
            errorMessage={errors?.subDistrict}
            onChange={(e) => {
              setFieldValue("subDistrict", e.target.value);
              handleSubDistrictChange(e, setFieldValue);
            }}
          />

          <TextInput
            label={t("editProfile.zipCode")}
            name={`details.${index}.zipCode`}
            disabled
            required
            placeholder={t("editProfile.zipCodePlaceholder")}
            maxLength={5}
            errorMessage={errors?.zipCode}
          />
        </>
      )}
    </VStack>
  );
};
